import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuChevronRight, LuFileText, LuHistory, LuSearchX } from 'react-icons/lu';
import { API_BASE_URL } from '../config.js';
import Changelog from '../components/Changelog.jsx';
import { SearchInput } from '../components/ui/SearchInput.jsx';
import { Card, CardHeader } from '../components/ui/Card.jsx';
import Avatar from '../components/ui/Avatar.jsx';
import Badge from '../components/ui/Badge.jsx';
import Alert from '../components/ui/Alert.jsx';
import EmptyState from '../components/ui/EmptyState.jsx';
import Skeleton from '../components/ui/Skeleton.jsx';
import Highlight from '../components/ui/Highlight.jsx';
import { sourceLabel, sourceTone } from '../utils/sources.js';

const normalize = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** One client the user can open: avatar, name, source, arrow. */
// Narrower grid when the "Nouveautés" column takes a third of the page.
const gridClass = (withSidePanel) => (withSidePanel
  ? 'grid grid-cols-1 gap-3 sm:grid-cols-2'
  : 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3');

const ReportCard = ({ config, query, isAdminUser }) => (
  <Link
    to={`/w/${config.slug}`}
    className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3.5 outline-none transition-all hover:-translate-y-px hover:border-brand-200 hover:shadow-md focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-900 dark:bg-night-800 dark:hover:border-brand-800"
  >
    <Avatar text={config.name} size="lg" />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-semibold text-gray-900 dark:text-white">
        <Highlight text={config.name} query={query} />
      </span>
      <span className="mt-1 flex flex-wrap gap-1.5">
        <Badge tone={sourceTone(config.source_type)} size="sm">{sourceLabel(config.source_type)}</Badge>
        {isAdminUser && config.is_source_active === false && <Badge tone="gray" size="sm" title="Le cron hebdomadaire ne récupère plus ce client">Source inactive</Badge>}
      </span>
    </span>
    <LuChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-500 dark:text-gray-600" aria-hidden />
  </Link>
);

function Home({ currentUser, fetchWithAuth }) {
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const isAdminUser = !!(currentUser?.isadmin || currentUser?.issuperadmin);
  const showChangelog = !!(currentUser?.isadmin || currentUser?.issuperuser);

  useEffect(() => {
    const fetchUserReports = async () => {
      try {
        if (currentUser) {
          const response = await fetchWithAuth(`${API_BASE_URL}/api/weekly/my`);
          if (!response.ok) throw new Error('Erreur lors de la récupération des configurations');
          const data = await response.json();
          setConfigs(data);

          // A client with a single report goes straight to it.
          if (data.length === 1 && !isAdminUser) {
            navigate(`/w/${data[0].slug}`);
          }
        }
      } catch (err) {
        console.error('Erreur:', err);
        setError('Impossible de charger vos rapports. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserReports();
  }, [currentUser, isAdminUser, fetchWithAuth, navigate]);

  const filteredConfigs = useMemo(() => {
    const sorted = [...configs].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    const q = normalize(searchQuery.trim());
    if (!q) return sorted;
    return sorted.filter((config) => normalize(`${config?.name} ${config?.slug} ${sourceLabel(config?.source_type)}`).includes(q));
  }, [configs, searchQuery]);

  useEffect(() => {
    if (!loading && configs.length !== 1) inputRef.current?.focus?.();
  }, [loading, configs.length]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') setSearchQuery('');
    if (e.key === 'Enter' && filteredConfigs.length === 1) navigate(`/w/${filteredConfigs[0].slug}`);
  };

  // If the user is being auto-redirected, don't render anything.
  if (currentUser && configs.length === 1 && !isAdminUser) {
    return null;
  }

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6" aria-busy="true">
        <Skeleton className="mb-6 h-8 w-64" />
        <div className={gridClass(showChangelog)}>
          {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-[74px] rounded-xl" />)}
        </div>
      </div>
    );
  }

  const reports = (
    <Card>
      <CardHeader
        icon={LuFileText}
        title="Vos rapports"
        badge={configs.length > 0 && <Badge tone="violet" size="sm">{configs.length}</Badge>}
        subtitle="Choisissez un client pour consulter ses rapports hebdomadaires"
        actions={configs.length > 1 && (
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            placeholder="Rechercher un client…"
            className="w-full sm:w-72"
          />
        )}
      />
      <div className="p-4 sm:p-5">
        {configs.length === 0 ? (
          <EmptyState
            icon={LuFileText}
            title="Aucun rapport accessible"
            description="Vous n'avez pas encore accès à des rapports. Contactez un administrateur pour obtenir des droits d'accès."
          />
        ) : filteredConfigs.length === 0 ? (
          <EmptyState icon={LuSearchX} tone="gray" title="Aucun résultat" description="Essayez un autre nom de client." />
        ) : (
          <>
            {filteredConfigs.length === 1 && searchQuery.trim() !== '' && (
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Appuyez sur Entrée pour ouvrir le rapport.</p>
            )}
            <div className={gridClass(showChangelog)}>
              {filteredConfigs.map((config) => (
                <ReportCard key={config.id} config={config} query={searchQuery.trim()} isAdminUser={isAdminUser} />
              ))}
            </div>
          </>
        )}
      </div>
    </Card>
  );

  return (
    <div className="min-h-full bg-gradient-to-b from-gray-50 to-white dark:from-night-950 dark:to-night-900">
      <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-6 sm:px-6">
        {error && <Alert tone="danger">{error}</Alert>}

        {showChangelog ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">{reports}</div>
            <div className="lg:col-span-1">
              <Card className="lg:sticky lg:top-6">
                <CardHeader icon={LuHistory} iconTone="gray" title="Nouveautés" subtitle="Dernières évolutions de Globe Weekly" />
                <div className="max-h-[70vh] overflow-y-auto p-4 sm:p-5">
                  <Changelog />
                </div>
              </Card>
            </div>
          </div>
        ) : reports}
      </div>
    </div>
  );
}

export default Home;
